import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Box,
  Divider,
  Flex,
  Input,
  Link,
  PrimaryButton,
  SecondaryButton,
  Stack,
  TransparentButton,
  Text, LegacyText,
  Spinner,
} from "flicket-ui";
import debounce from "lodash/debounce";
// eslint-disable-next-line import/no-extraneous-dependencies
import qs from "qs";
import { css } from "styled-components";
import { ModalProps, Modal } from "~components";
import { useAccount, useShopifyCheckout } from "~hooks";
import { showToast } from "~lib";
import { Icon } from "~components/common";
import { CheckoutBaseFragmentFragment } from "~lib/shopify/sdk";

const CartLineItem = (
  lineItem: CheckoutBaseFragmentFragment["lineItems"]["edges"][number]["node"]
) => {
  const {
    adjustLineItemQuantity,
    removeLineItem,
    checkout,
  } = useShopifyCheckout();
  const [quantity, setQuantity] = useState(lineItem.quantity);

  const debouncedAdjustQuantity = useCallback(
    debounce(adjustLineItemQuantity, 500),
    [checkout]
  );

  useEffect(() => {
    if (quantity !== lineItem.quantity) {
      debouncedAdjustQuantity({ variantId: lineItem.variant.id, quantity });
    }
  }, [quantity, lineItem.quantity]);

  const showToManyToast = () =>
    showToast(
      `Only ${lineItem?.variant?.quantityAvailable} available`,
      "error"
    );

  return (
    <Flex>
      <Box
        borderRadius="md"
        borderStyle="solid"
        borderWidth={1 as any}
        borderColor="N300"
        width={80}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundImage={`url(${lineItem?.variant?.image?.url})`}
      />
      <Flex flexDir="column" ml="6/4" maxWidth={{ _: 200, md: 400 }}>
        <LegacyText
          color="N800"
          variant="extraBold.L"
          letterSpacing={"-0.17px" as any}
          lineHeight="medium"
          mb={"1/2"}
        >
          {lineItem.title}
        </LegacyText>
        <Box flex={1}>
          {lineItem?.variant?.selectedOptions?.map(({ name, value }, i) => (
            <LegacyText color="N800" fontSize={3} key={i}>
              {name}: {value}
            </LegacyText>
          ))}
        </Box>
        <Flex
          mt={1}
          alignItems="center"
          css={`
            input {
              height: 32px;
              padding: 4px;
              text-align: center;
              font-size: 12px;
            }
          `}
        >
          <LegacyText
            color="N700"
            fontWeight="demiBold"
            letterSpacing={"-0.165px" as any}
          >
            Qty
          </LegacyText>
          <Icon
            cursor="pointer"
            onClick={() => quantity > 1 && setQuantity(quantity - 1)}
            ml="6/4"
            fill="N500"
            icon="minus"
          />
          <Input
            p={0}
            width={42 + (String(quantity).length - 1) * 8}
            ml="6/4"
            type="number"
            readOnly
            min="1"
            value={quantity}
          />
          <Icon
            cursor="pointer"
            onClick={() =>
              lineItem.variant.quantityAvailable > quantity
                ? setQuantity(quantity + 1)
                : showToManyToast()
            }
            ml="6/4"
            fill="N500"
            icon="plus"
          />
        </Flex>
      </Flex>
      <Flex ml={"auto" as any} alignItems="flex-end" flexDir="column">
        <Icon
          onClick={() => removeLineItem({ variantId: lineItem.variant.id })}
          cursor="pointer"
          fontSize={20 as any}
          icon="remove-circle"
          css={css`
            transition: opacity 0.2s ease-in-out;
            &:hover {
              opacity: 0.7;
            }
          `}
        />
        <LegacyText
          fontSize={4}
          color="N600"
          fontWeight="extraBold"
          mt={"auto" as any}
          lineHeight="normal"
        >
          ${(Number(lineItem?.variant?.priceV2?.amount) * quantity).toFixed(2)}
        </LegacyText>
      </Flex>
    </Flex>
  );
};

export const CartModal = (props: ModalProps) => {
  const { checkout, updatingCart } = useShopifyCheckout();
  const { user } = useAccount();

  const userShopifyCheckoutParameters = user
    ? "&" +
      qs.stringify({
        checkout: {
          email: user?.email,
          shipping_address: {
            first_name: user?.firstName,
            last_name: user?.lastName,
            address1: user?.shippingAddress?.line1,
            address2: user?.shippingAddress?.line2,
            city: user?.shippingAddress?.city,
            zip: user?.shippingAddress?.postalCode,
          },
        },
      })
    : "";

  return (
    <Modal
      {...props}
      backgroundColor="transparent"
      maxWidth={{ _: "auto", md: "720px" }}
      p={{ _: 2, md: 0 }}
    >
      <Flex
        flexDir="column"
        width="100%"
        borderRadius="md"
        backgroundColor="white"
        p={{ _: 2, md: 4 }}
        height={{ _: "auto", md: "calc(100vh - 64px)" }}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <LegacyText
            lineHeight="normal"
            fontSize={6}
            fontWeight="extraBold"
            color="N800"
          >
            Cart
          </LegacyText>
          <TransparentButton onClick={props.onClose}>
            <Icon icon="close" fontSize={6} color="N800" />
          </TransparentButton>
        </Flex>
        <Divider mt={2} />
        <Stack
          overflowY={{ _: "visible", md: "auto" }}
          flexDir="column"
          space={2}
          pb={2}
        >
          {checkout?.lineItems?.edges?.length ? (
            checkout?.lineItems?.edges?.map(({ node }, index) => (
              <Fragment key={node.id}>
                <Divider display={index === 0 ? "none" : "flex"} />
                <CartLineItem {...node} />
              </Fragment>
            ))
          ) : (
            <LegacyText mt={2}>You have no items in your cart</LegacyText>
          )}
        </Stack>
        <Divider />
        <Box py={2} fontSize={4} ml={"auto" as any}>
          {!!Number(checkout?.totalPriceV2?.amount) && (
            <>
              <LegacyText color="N800" display="inline-block">
                Total
              </LegacyText>
              <LegacyText
                position="relative"
                color="N600"
                fontWeight="extraBold"
                ml={2}
                display="inline-block"
              >
                ${Number(checkout?.totalPriceV2?.amount).toFixed(2)}
                {updatingCart && (
                  <Flex
                    variant="center"
                    position="absolute"
                    top={0}
                    right={0}
                    bottom={0}
                    left={0}
                    backgroundColor={"rgba(255,255,255,.8)" as any}
                  >
                    <Spinner color="P300" size={20} />
                  </Flex>
                )}
              </LegacyText>
            </>
          )}
        </Box>
        <Flex flex="1" />
        <Divider mb={4} display={{ _: "none", md: "flex" }} />
        <Stack
          space={2}
          width="100%"
          flexDir={["column", "column", "column", "column", "row"]}
        >
          <SecondaryButton onClick={props.onClose} flex={1}>
            Continue Shopping
          </SecondaryButton>
          {checkout?.webUrl && (
            <Link
              flex={1}
              href={checkout.webUrl + userShopifyCheckoutParameters}
            >
              <PrimaryButton
                disabled={!checkout?.lineItems?.edges?.length}
                width="100%"
              >
                Checkout
              </PrimaryButton>
            </Link>
          )}
        </Stack>
      </Flex>
    </Modal>
  );
};
